/**
 * Queries/mutations do PORTAL DO CLIENTE (/api/v1/portal/*, bearer normal —
 * o backend valida role client) e da avaliação CSAT PÚBLICA
 * (/api/v1/public/rate/:token, fetch SEM bearer e sem o handler de 401 do
 * api(): a página roda fora do AuthGate e um token expirado no storage jamais
 * pode disparar o fluxo de login do visitante — mesmo racional do public-kb).
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiList, type ApiError } from '@/lib/api-client';
import { env } from '@/config/env';
import type {
  PortalComment,
  PortalDesk,
  PortalNewTicketBody,
  PortalSummary,
  PortalTicketDetail,
  PortalTicketListItem,
  PublicFormSubmit,
  PublicFormView,
  PublicRatingView,
  RateBody,
} from '@erp/shared';

export const PORTAL_KEY = ['erp', 'portal'] as const;

// ── Queries (autenticadas, role client) ─────────────────────────────────────

export function portalSummaryQuery() {
  return queryOptions({
    queryKey: ['erp', 'portal', 'summary'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<PortalSummary>('/portal/summary', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export interface PortalTicketsParams {
  status?: 'open' | 'closed' | 'canceled' | 'all';
  search?: string;
  limit?: number;
  offset?: number;
}

/** Lista paginada — o total vem do header X-Total-Items (padrão apiList). */
export function portalTicketsQuery(params: PortalTicketsParams) {
  return queryOptions({
    queryKey: ['erp', 'portal', 'tickets', 'list', params] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<PortalTicketListItem>('/portal/tickets', {
        query: { ...params },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export const portalTicketKey = (ticketNumber: number) =>
  ['erp', 'portal', 'tickets', 'detail', ticketNumber] as const;

export function portalTicketQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: portalTicketKey(ticketNumber),
    queryFn: async ({ signal }) => {
      const res = await api<PortalTicketDetail>(`/portal/tickets/${ticketNumber}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Mesas portal_enabled — as únicas em que o cliente pode abrir ticket. */
export function portalDesksQuery() {
  return queryOptions({
    queryKey: ['erp', 'portal', 'desks'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<PortalDesk>('/portal/desks', { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
    staleTime: 60_000,
  });
}

// ── Mutations (autenticadas) ────────────────────────────────────────────────

export function useCreatePortalTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PortalNewTicketBody) => {
      const res = await api<PortalTicketDetail>('/portal/tickets', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PORTAL_KEY }),
  });
}

export function usePortalComment(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await api<PortalComment>(`/portal/tickets/${ticketNumber}/comments`, {
        method: 'POST',
        body: { body },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: portalTicketKey(ticketNumber) }),
  });
}

// ── Avaliação CSAT pública (/public/rate/:token — SEM bearer) ───────────────
// Base URL igual ao api(): env.apiBaseUrl vazio = mesma origem (proxy do Vite
// no dev); absoluto = API em outro host (ADR-018, caso da produção).

async function publicApi<T>(
  path: string,
  opts: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal } = {}
): Promise<T> {
  const url = new URL(`${env.apiBaseUrl}/api/v1/public${path}`, window.location.origin);
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal ?? AbortSignal.timeout(30_000),
    });
  } catch (err) {
    throw {
      status: 0,
      error: err instanceof Error ? err.message : 'network_error',
    } satisfies ApiError;
  }
  if (!res.ok) {
    let body: { error?: string; details?: unknown } = {};
    try {
      body = (await res.json()) as { error?: string; details?: unknown };
    } catch {
      /* corpo de erro não-JSON */
    }
    throw {
      status: res.status,
      error: body.error ?? `http_${res.status}`,
      details: body.details,
    } satisfies ApiError;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function publicRatingQuery(token: string) {
  return queryOptions({
    queryKey: ['erp', 'public-rate', token] as const,
    queryFn: ({ signal }) =>
      publicApi<PublicRatingView>(`/rate/${encodeURIComponent(token)}`, { signal }),
    retry: false,
  });
}

export function usePublicRate(token: string) {
  return useMutation({
    mutationFn: (body: RateBody) =>
      publicApi<unknown>(`/rate/${encodeURIComponent(token)}`, { method: 'POST', body }),
  });
}

// ── Formulário externo público (/public/form/:token — SEM bearer, v5) ───────

export function publicFormQuery(token: string) {
  return queryOptions({
    queryKey: ['erp', 'public-form', token] as const,
    queryFn: ({ signal }) =>
      publicApi<PublicFormView>(`/form/${encodeURIComponent(token)}`, { signal }),
    retry: false,
  });
}

export function usePublicFormSubmit(token: string) {
  return useMutation({
    mutationFn: (body: PublicFormSubmit) =>
      publicApi<unknown>(`/form/${encodeURIComponent(token)}`, { method: 'POST', body }),
  });
}

// ── Helper de erro (ApiError-shaped) — regra única em features/tickets ──────

export { errorMessage } from '@/features/tickets/ticket-format';
